import React, { useState, useEffect } from 'react';
import { Box, VStack, Button, Input, Text, Heading, useToast, InputGroup, InputRightElement, Tabs, TabList, TabPanels, Tab, TabPanel, Center, Divider } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock, FaQrcode, FaUser } from 'react-icons/fa';
import { useApp } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';
import QRCodeBox from '../components/QRCodeBox';

const MotionBox = motion(Box);

const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [adminToken, setAdminToken] = useState('');
  const [qrCodeValue, setQrCodeValue] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { loginUser } = useApp();
  
  // Gerar token único para o QR Code - apenas uma vez na montagem do componente
  useEffect(() => {
    // Verificar se já temos um token
    if (adminToken) return;
    
    const generateToken = async () => {
      // Gerar um token único para esta sessão
      const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      setAdminToken(token);
      
      // Criar URL para o QR Code que inclui o token
      const baseUrl = window.location.origin;
      const qrCodeUrl = `${baseUrl}/admin-mobile-login?token=${token}`;
      setQrCodeValue(qrCodeUrl);
      
      // Salvar o token no Supabase para verificação posterior
      try {
        await supabase.from('admin_login_tokens').insert({
          token,
          created_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString(), // Expira em 5 minutos
          used: false
        });
      } catch (error) {
        console.error('Erro ao salvar token:', error);
      }
    };
    
    generateToken();
  }, []); // Sem dependências para executar apenas uma vez
  
  // Configurar listener para verificar se o token foi usado
  useEffect(() => {
    if (!adminToken) return; // Só configura o listener quando temos um token
    
    const channel = supabase
      .channel('admin-login')
      .on('broadcast', { event: 'admin-login' }, async (payload) => {
        if (payload.payload.token === adminToken) {
          // Token foi usado, fazer login automático
          const { userId, userName, userEmail } = payload.payload;
          
          // Login como administrador
          loginUser({
            name: userName || 'Administrador',
            email: userEmail,
            id: userId,
          }, true); // true indica que é um admin
          
          toast({
            title: 'Login realizado com sucesso',
            description: 'Autenticado via dispositivo móvel',
            status: 'success',
            duration: 2000,
          });
          
          // Redirecionar para o menu principal
          navigate('/menu');
        }
      })
      .subscribe();
    
    // Limpar o canal ao desmontar
    return () => {
      channel.unsubscribe();
    };
  }, [adminToken, loginUser, navigate, toast]);

  const handleLogin = async () => {
    if (!email || !password) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Verificar credenciais de administrador no Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data && data.user) {
        // Verificar se o usuário é um administrador
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', data.user.id)
          .single();

        if (adminError || !adminData) {
          throw new Error('Usuário não tem permissão de administrador');
        }

        // Login como administrador
        loginUser({
          name: adminData.nome || 'Administrador',
          email: data.user.email,
          id: data.user.id,
        }, true); // true indica que é um admin

        toast({
          title: 'Login realizado com sucesso',
          status: 'success',
          duration: 2000,
        });

        // Redirecionar para o menu principal
        navigate('/menu');
      }
    } catch (error) {
      toast({
        title: 'Erro no login',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(26,32,44,0.95) 100%)"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={8}
      position="relative"
      overflow="hidden"
    >
      {/* Efeito de brilho no fundo */}
      <Box
        position="absolute"
        top="-50%"
        left="-50%"
        width="200%"
        height="200%"
        background="radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)"
        transform="rotate(-45deg)"
        pointerEvents="none"
      />

      <Box 
        width="100%" 
        maxWidth="450px" 
        bg="gray.900" 
        p={6} 
        borderRadius="xl" 
        boxShadow="0 4px 30px rgba(0, 0, 0, 0.4)"
        border="1px solid"
        borderColor="yellow.500"
      >
        <VStack spacing={4} align="stretch">
          <Heading 
            color="yellow.400" 
            textAlign="center" 
            size="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={2}
          >
            <FaLock style={{ marginRight: '10px' }} /> Acesso Administrativo
          </Heading>
          
          <Tabs isFitted variant="enclosed" colorScheme="yellow">
            <TabList mb="1em">
              <Tab _selected={{ color: 'yellow.400', borderColor: 'yellow.400' }}>
                <Box display="flex" alignItems="center">
                  <FaQrcode style={{ marginRight: '8px' }} />
                  <Text>QR Code</Text>
                </Box>
              </Tab>
              <Tab _selected={{ color: 'yellow.400', borderColor: 'yellow.400' }}>
                <Box display="flex" alignItems="center">
                  <FaUser style={{ marginRight: '8px' }} />
                  <Text>Login Manual</Text>
                </Box>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <VStack spacing={4} align="center">
                  <Text color="gray.300" textAlign="center">
                    Escaneie o QR Code com seu celular para fazer login como administrador
                  </Text>
                  
                  <Box>
                    <QRCodeBox url={qrCodeValue} />
                  </Box>
                  
                  <Text color="gray.400" fontSize="sm" textAlign="center">
                    Este código expira em 5 minutos por segurança
                  </Text>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Input
                    placeholder="E-mail"
                    size="lg"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: 'whiteAlpha.500' }}
                    _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
                  />
                  
                  <InputGroup size="lg">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor="gray.600"
                      color="white"
                      _placeholder={{ color: 'whiteAlpha.500' }}
                      _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
                    />
                    <InputRightElement>
                      <Button 
                        h="1.75rem" 
                        size="sm" 
                        onClick={() => setShowPassword(!showPassword)}
                        bg="transparent"
                        _hover={{ bg: 'whiteAlpha.200' }}
                      >
                        {showPassword ? <FaEyeSlash color="gray" /> : <FaEye color="gray" />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  
                  <Button
                    colorScheme="yellow"
                    size="lg"
                    onClick={handleLogin}
                    isLoading={loading}
                    loadingText="Autenticando..."
                  >
                    Entrar
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
          
          <Divider my={2} />
          
          <Button
            variant="ghost"
            colorScheme="whiteAlpha"
            onClick={handleBack}
            size="md"
          >
            Voltar
          </Button>
        </VStack>
      </Box>
      
      <Text color="gray.500" fontSize="sm" mt={8} textAlign="center">
        Acesso restrito para administradores da Barbearia Freire
      </Text>
    </MotionBox>
  );
};

export default AdminLoginPage;
